// src/components/rcv/asociaciones/ImpuestosAdicionalesSection.jsx
import React, { useState } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Box,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import SaveIcon from '@mui/icons-material/Save';
import { formatearMonto } from '@/utils/formatters';
import { asociacionesService } from '@/services/asociacionesService';

const ImpuestosAdicionalesSection = ({ 
  documentoSeleccionado, 
  cuentas, 
  centros, 
  clasificadores,
  onSuccess,
  onError 
}) => {
  // Si no hay documento seleccionado o no tiene impuestos adicionales, no mostramos nada
  if (!documentoSeleccionado || !documentoSeleccionado.otros_impuestos || documentoSeleccionado.otros_impuestos.length === 0) {
    return null;
  }

  const impuestos = documentoSeleccionado.otros_impuestos;
  
  return (
    <Accordion sx={{ mt: 3 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="impuestos-adicionales-content"
        id="impuestos-adicionales-header"
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PriceChangeIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1">
            Impuestos Adicionales ({impuestos.length})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" paragraph>
          Asocie cuentas, centros y clasificadores específicos para cada impuesto adicional.
        </Typography>
        
        <ImpuestosTable 
          impuestos={impuestos}
          cuentas={cuentas} 
          centros={centros} 
          clasificadores={clasificadores}
          onSuccess={onSuccess}
          onError={onError}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const ImpuestosTable = ({ impuestos, cuentas, centros, clasificadores, onSuccess, onError }) => {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Código</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="right">Tasa</TableCell>
            <TableCell>Cuenta</TableCell>
            <TableCell>Centro</TableCell>
            <TableCell>Clasificador</TableCell>
            <TableCell align="center">Acción</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {impuestos.map((impuesto) => (
            <ImpuestoRow 
              key={impuesto.id} 
              impuesto={impuesto}
              cuentas={cuentas} 
              centros={centros} 
              clasificadores={clasificadores}
              onSuccess={onSuccess}
              onError={onError}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ImpuestoRow = ({ impuesto, cuentas, centros, clasificadores, onSuccess, onError }) => {
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(impuesto.cuenta_id ? String(impuesto.cuenta_id) : '');
  const [centroSeleccionado, setCentroSeleccionado] = useState(impuesto.centro_id ? String(impuesto.centro_id) : '');
  const [clasificadorSeleccionado, setClasificadorSeleccionado] = useState(impuesto.clasificador_id ? String(impuesto.clasificador_id) : '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const handleGuardarAsociacion = async () => {
    if (!cuentaSeleccionada && !centroSeleccionado && !clasificadorSeleccionado) {
      setError('Debe seleccionar al menos una cuenta, centro o clasificador');
      return;
    }

    setGuardando(true);
    setError('');

    try {
      const data = {
        cuenta_id: cuentaSeleccionada ? parseInt(cuentaSeleccionada) : null,
        centro_id: centroSeleccionado ? parseInt(centroSeleccionado) : null,
        clasificador_id: clasificadorSeleccionado ? parseInt(clasificadorSeleccionado) : null
      };

      await asociacionesService.actualizarAsociacionImpuesto(impuesto.id, data);
      
      // Notificar éxito
      if (onSuccess) {
        onSuccess(`Impuesto ${impuesto.codigo_impuesto} asociado correctamente`);
      }
    } catch (err) {
      setError(err.message || 'Error al guardar la asociación');
      if (onError) {
        onError(err.message || 'Error al guardar la asociación');
      }
    } finally {
      setGuardando(false);
    }
  };

  // IDs únicos para los campos de este impuesto
  const cuentaId = `cuenta-${impuesto.id}`;
  const centroId = `centro-${impuesto.id}`;
  const clasificadorId = `clasificador-${impuesto.id}`;
  const cuentaLabelId = `cuenta-label-${impuesto.id}`;
  const centroLabelId = `centro-label-${impuesto.id}`;
  const clasificadorLabelId = `clasificador-label-${impuesto.id}`;
  const btnId = `guardar-btn-${impuesto.id}`;

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          {impuesto.codigo_impuesto || '-'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontFamily="monospace">
          {formatearMonto(impuesto.valor_impuesto)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">
          {impuesto.tasa_impuesto ? `${impuesto.tasa_impuesto}%` : '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
          <InputLabel id={cuentaLabelId}>Cuenta</InputLabel>
          <Select
            labelId={cuentaLabelId}
            id={cuentaId}
            name={cuentaId}
            value={cuentaSeleccionada}
            label="Cuenta"
            onChange={(e) => setCuentaSeleccionada(e.target.value)}
            disabled={guardando}
            aria-labelledby={cuentaLabelId}
          >
            <MenuItem value="">Sin Cuenta</MenuItem>
            {cuentas.map((cuenta) => (
              <MenuItem key={cuenta.id} value={cuenta.id.toString()}>
                {cuenta.codigo} - {cuenta.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
          <InputLabel id={centroLabelId}>Centro</InputLabel>
          <Select
            labelId={centroLabelId}
            id={centroId}
            name={centroId}
            value={centroSeleccionado}
            label="Centro"
            onChange={(e) => setCentroSeleccionado(e.target.value)}
            disabled={guardando}
            aria-labelledby={centroLabelId}
          >
            <MenuItem value="">Sin Centro</MenuItem>
            {centros.map((centro) => (
              <MenuItem key={centro.id} value={centro.id.toString()}>
                {centro.codigo} - {centro.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell>
        <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
          <InputLabel id={clasificadorLabelId}>Clasificador</InputLabel>
          <Select
            labelId={clasificadorLabelId}
            id={clasificadorId}
            name={clasificadorId}
            value={clasificadorSeleccionado}
            label="Clasificador"
            onChange={(e) => setClasificadorSeleccionado(e.target.value)}
            disabled={guardando}
            aria-labelledby={clasificadorLabelId}
          >
            <MenuItem value="">Sin Clasificador</MenuItem>
            {clasificadores.map((clasificador) => (
              <MenuItem key={clasificador.id} value={clasificador.id.toString()}>
                {clasificador.codigo} - {clasificador.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell align="center">
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleGuardarAsociacion}
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={16} /> : <SaveIcon />}
          id={btnId}
          name={btnId}
        >
          {guardando ? 'Guardando...' : 'Guardar'}
        </Button>
        
        {error && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="error" size="small">
              {error}
            </Alert>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

export default ImpuestosAdicionalesSection;